import Vue from "vue";
import VueRouter from "vue-router";
import LightBootstrap from "./light-bootstrap-main";
import "./assets/styles/shift-colors.css";
import "./assets/styles/global.css";
import { createPinia, PiniaVuePlugin } from "pinia";
import { createPersistedState } from "pinia-plugin-persistedstate";
import "@fortawesome/fontawesome-free/css/all.css";
import GlobalComponents from "./globalComponents";
import App from "./App.vue";
import PortalVue from "portal-vue";

import spinnerPlugin from "@/plugins/spinner";
import modalPlugin from "@/plugins/modal";
import clockNudgePlugin from "@/plugins/clockNudge";

import router from "./routes/routes";
import { useSpinnerStore } from "@/stores/spinner";

Vue.use(VueRouter);
Vue.use(LightBootstrap);
Vue.use(PiniaVuePlugin);
Vue.use(GlobalComponents);
Vue.use(PortalVue);

const pinia = createPinia();
pinia.use(
  createPersistedState({
    storage: localStorage,
  }),
);
Vue.use(pinia);
Vue.use(spinnerPlugin);
Vue.use(modalPlugin);
Vue.use(clockNudgePlugin);

// --- Global error safety net -------------------------------------------------
// Vue 2 has no error boundary: an uncaught error logs to the console and then
// nothing resets global UI state, so a full-screen overlay or an orphaned modal
// backdrop can leave the whole app (mobile menu included) permanently
// unclickable. These handlers self-heal that state instead of letting it strand
// the user, and log the error so we can see it happened.
function recoverUiState() {
  // Always safe: the loading overlay is transient, so drop it on any error.
  try {
    useSpinnerStore(pinia).reset();
  } catch (e) {
    /* pinia may not be ready — ignore */
  }
  // Only clear a modal backdrop / body scroll-lock when it's genuinely
  // orphaned (no dialog actually visible). This avoids stripping the backdrop
  // out from under a dialog that is legitimately open.
  try {
    const hasVisibleDialog = Array.from(
      document.querySelectorAll(".el-dialog__wrapper"),
    ).some((el) => el.style.display !== "none");
    if (!hasVisibleDialog) {
      document.body.classList.remove("el-popup-parent--hidden", "modal-open");
      document.querySelectorAll(".v-modal").forEach((el) => el.remove());
    }
  } catch (e) {
    /* DOM cleanup is best-effort — ignore */
  }
}

Vue.config.errorHandler = (err, vm, info) => {
  // eslint-disable-next-line no-console
  console.error("[GlobalError] Vue error", info, err);
  recoverUiState();
};

window.addEventListener("error", (event) => {
  // Ignore resource-load errors (a broken <img>/<script>/<link>, e.g. a 404
  // avatar): those aren't app faults and must not reset UI state. Only real
  // uncaught JS exceptions target `window`.
  const target = event.target;
  if (target && target !== window && target.tagName) {
    return;
  }
  // eslint-disable-next-line no-console
  console.error("[GlobalError] window error", event.error || event.message);
  recoverUiState();
});

window.addEventListener("unhandledrejection", (event) => {
  // eslint-disable-next-line no-console
  console.error("[GlobalError] unhandled rejection", event.reason);
  recoverUiState();
});

/* eslint-disable no-new */
new Vue({
  el: "#app",
  render: (h) => h(App),
  router,
  pinia,
});
